/**
 * What a ComBox / Mini factory reset (command 102) resets, one bit each, as the IPU firmware defines
 * them (ComBox `ipu-g-firmware` 4.1.22 and Mini `ipu-k-firmware` 1.0.9, `CMD_RESET_FLAG_*`).
 *
 * Two bits are deliberately missing: bit 2 is reserved, and bit 6 (F config) also resets the
 * device's serial number, BLE MAC and modem IMEI, so the box would report under a factory identity
 * and drop out of the portal until it is provisioned again (Taimoor, 2026-10-09).
 */
export enum ComBoxFactoryResetFlag {
  /** Deletes every run stored on the box and starts its run list afresh. */
  DATALOG = 1 << 0,
  /** Forgets the stored files (logo, firmware images). */
  FILE_INFO = 1 << 1,
  /** Drying and sensor programs back to factory defaults. */
  D_CONFIG = 1 << 3,
  /** Client config back to factory defaults. */
  C_CONFIG = 1 << 4,
  /** Base config back to factory defaults. */
  B_CONFIG = 1 << 5,
  /** Operating hours and power-up counter back to zero. */
  COUNTERS = 1 << 7,
}

/** Flags after which the firmware reboots the box once it has answered. */
export const REBOOTING_FACTORY_RESET_FLAGS: ComBoxFactoryResetFlag[] = [
  ComBoxFactoryResetFlag.D_CONFIG,
  ComBoxFactoryResetFlag.C_CONFIG,
  ComBoxFactoryResetFlag.B_CONFIG,
  ComBoxFactoryResetFlag.COUNTERS,
];

/**
 * Flags after which the box restarts: the ones the firmware reboots for, and the datalog, after
 * which Office queues a NEUSTART itself (see `needsRestartAfterFactoryReset`).
 */
const RESTARTING_FACTORY_RESET_FLAGS: ComBoxFactoryResetFlag[] = [
  ComBoxFactoryResetFlag.DATALOG,
  ...REBOOTING_FACTORY_RESET_FLAGS,
];

/**
 * Whether Office queues a NEUSTART behind the reset. The box only takes an emptied run list after
 * a restart: until then a drying started at the display opens no run (BE772 and BE206, 2026-10).
 * A config or counter reset reboots the box by itself, so no second restart is queued then.
 */
export function needsRestartAfterFactoryReset(flags: number): boolean {
  const rebootsItself = REBOOTING_FACTORY_RESET_FLAGS.some((flag) => (flags & flag) !== 0);

  return (flags & ComBoxFactoryResetFlag.DATALOG) !== 0 && !rebootsItself;
}

/** Every flag Office may send; anything else is refused before it reaches a box. */
export const ALLOWED_FACTORY_RESET_FLAGS = [
  ComBoxFactoryResetFlag.DATALOG,
  ComBoxFactoryResetFlag.FILE_INFO,
  ComBoxFactoryResetFlag.D_CONFIG,
  ComBoxFactoryResetFlag.C_CONFIG,
  ComBoxFactoryResetFlag.B_CONFIG,
  ComBoxFactoryResetFlag.COUNTERS,
].reduce((mask, flag) => mask | flag, 0);

/** One checkbox in the reset dialog, in the order shown. */
export interface FactoryResetOption {
  flag: ComBoxFactoryResetFlag;
  /** Key under `comBoxSettings.factoryReset.flags`. */
  key: string;
  reboots: boolean;
}

export const FACTORY_RESET_OPTIONS: FactoryResetOption[] = [
  { flag: ComBoxFactoryResetFlag.DATALOG, key: "datalog" },
  { flag: ComBoxFactoryResetFlag.FILE_INFO, key: "fileInfo" },
  { flag: ComBoxFactoryResetFlag.D_CONFIG, key: "dConfig" },
  { flag: ComBoxFactoryResetFlag.C_CONFIG, key: "cConfig" },
  { flag: ComBoxFactoryResetFlag.B_CONFIG, key: "bConfig" },
  { flag: ComBoxFactoryResetFlag.COUNTERS, key: "counters" },
].map((option) => ({
  ...option,
  reboots: RESTARTING_FACTORY_RESET_FLAGS.includes(option.flag),
}));

/** The ticked flags as the one byte command 102 carries. */
export function combineFactoryResetFlags(flags: ComBoxFactoryResetFlag[]): number {
  return flags.reduce((mask, flag) => mask | flag, 0);
}

/**
 * What the reset does, as keys under `comBoxSettings.factoryReset.consequences`, in the order the
 * dialog's warning lists them. The drying is always ended first (the firmware refuses the reset
 * while one runs), and the commands always wait for the box's next contact.
 */
export function getFactoryResetConsequenceKeys(flags: ComBoxFactoryResetFlag[]): string[] {
  const keys = ["terminate"];

  if (flags.includes(ComBoxFactoryResetFlag.DATALOG)) {
    keys.push("datalog");
  }

  if (flags.some((flag) => RESTARTING_FACTORY_RESET_FLAGS.includes(flag))) {
    keys.push("reboot");
  }

  keys.push("queued");

  return keys;
}
